/**
 * All exchanges supported on Cede Hub platform.
 * Includes CEX, perps DEX (Hyperliquid), and onchain DEX (Aerodrome).
 *
 * Used for: Hub UI, quests, trade reporting, campaign eligibility.
 *
 * @note This is DIFFERENT from @cede/utils SUPPORTED_EXCHANGES which only has 6 CEX for SDK token-selling.
 * @see SUPPORTED_CEX for CEX-only subset (excludes Hyperliquid and Aerodrome)
 */
export const SUPPORTED_EXCHANGES = {
  // CEX - Centralized Exchanges
  KUCOIN: "kucoin",
  GATEIO: "gateio",
  MEXC: "mexc",
  BITGET: "bitget",
  BINANCE: "binance",
  BYBIT: "bybit",
  COINBASE: "coinbase",
  BITMART: "bitmart",
  OKX: "okx",
  CRYPTOCOM: "cryptocom",
  KRAKEN: "kraken",
  KRAKEN_FUTURES: "krakenfutures",
  BITHUMB: "bithumb",
  HTX: "huobi",
  LBANK: "lbank",
  // Perps DEX - Decentralized perpetuals exchange
  HYPERLIQUID: "hyperliquid",
  TRADEXYZ: "tradexyz",
  FLX: "flx",
  VNTL: "vntl",
  HYNA: "hyna",
  KM: "km",
  CASH: "cash",
  PARA: "para",
  MKTS: "mkts",
  PARADEX: "paradex",
  GRVT: "grvt",
  ZEROONE: "zeroone",
  EXTENDED: "extended",
  HIBACHI: "hibachi",
  DECIBEL: "decibel",
  HOTSTUFF: "hotstuff",
  DANGO: "dango",
  KATANA: "katana",
  RISE: "rise",
  // Paper trading - simulated perps against live Hyperliquid market data
  PAPER: "paper",
  // Prediction Market - Decentralized prediction market
  POLYMARKET: "polymarket",
  // Onchain DEX - Format: {protocol}|{chain}
  UNISWAP_V3_ETHEREUM: "uniswap-v3|ethereum",
  AERODROME_CL_BASE: "aerodrome-cl|base",
  AERODROME_CL_2_BASE: "aerodrome-cl-2|base",
  PANCAKE_V3_ARBITRUM: "pancake-v3|arbitrum",
  PANCAKE_V3_BASE: "pancake-v3-base|base",
  PANCAKE_INFINITY_BSC: "pancake-infinity-bsc|bsc",
  UNISWAP_V4_ROBINHOOD: "uniswap-v4-robinhood|robinhood",
  // Solana DEXes
  METEORA_SOLANA: "meteora|solana",
  METEORA_DYN_SOLANA: "meteora-dyn|solana",
  METEORA_DYN2_SOLANA: "meteora-dyn2|solana",
  METEORA_DBC_SOLANA: "meteora-dbc|solana",
  RAYDIUM_SOLANA: "raydium|solana",
  RAYDIUM_CLMM_SOLANA: "raydium-clmm|solana",
  RAYDIUM_CPMM_SOLANA: "raydium-cpmm|solana",
  RAYDIUM_LAUNCHLAB_SOLANA: "raydium-launchlab|solana",
  ORCA_SOLANA: "orca|solana",
  PUMPSWAP_SOLANA: "pumpswap|solana",
  JUPITER_STUDIO_SOLANA: "jupiter-studio|solana",
} as const;
export type SUPPORTED_EXCHANGES = (typeof SUPPORTED_EXCHANGES)[keyof typeof SUPPORTED_EXCHANGES];

/**
 * Exchanges requiring the setup (excludes onchain DEXs like Aerodrome)
 * Use this for features that only apply to centralized exchanges (API key linking, etc.)
 */
export const SUPPORTED_CEX = {
  KUCOIN: "kucoin",
  GATEIO: "gateio",
  MEXC: "mexc",
  BITGET: "bitget",
  BINANCE: "binance",
  BYBIT: "bybit",
  COINBASE: "coinbase",
  BITMART: "bitmart",
  OKX: "okx",
  CRYPTOCOM: "cryptocom",
  KRAKEN: "kraken",
  BITHUMB: "bithumb",
  KRAKEN_FUTURES: "krakenfutures",
  HTX: "huobi",
  LBANK: "lbank",
} as const;
export type SUPPORTED_CEX = (typeof SUPPORTED_CEX)[keyof typeof SUPPORTED_CEX];

/**
 * Exchanges that support the CEX affiliate/referral tracking feature.
 * Used for exclusive campaigns where only users registered via Cede referral links can participate.
 * @see apps/api/src/modules/exchange-account/CLAUDE.md for architecture details
 */
export const REFERRAL_SUPPORTED_EXCHANGES = Object.freeze([
  SUPPORTED_EXCHANGES.KUCOIN,
  SUPPORTED_EXCHANGES.GATEIO,
  SUPPORTED_EXCHANGES.BITGET,
  SUPPORTED_EXCHANGES.BYBIT,
  SUPPORTED_EXCHANGES.CRYPTOCOM,
  SUPPORTED_EXCHANGES.LBANK,
] as const);

/**
 * Perps exchanges that support the Airdrop Farming feature (grid trading bots).
 */
export const SUPPORTED_AIRDROP_FARMING_PERPS_EXCHANGES = Object.freeze([
  SUPPORTED_EXCHANGES.TRADEXYZ,
  SUPPORTED_EXCHANGES.PARADEX,
  SUPPORTED_EXCHANGES.GRVT,
  SUPPORTED_EXCHANGES.HYPERLIQUID,
  SUPPORTED_EXCHANGES.ZEROONE,
  SUPPORTED_EXCHANGES.EXTENDED,
  SUPPORTED_EXCHANGES.HIBACHI,
  SUPPORTED_EXCHANGES.HOTSTUFF,
  SUPPORTED_EXCHANGES.BITGET,
  SUPPORTED_EXCHANGES.DECIBEL,
  SUPPORTED_EXCHANGES.RISE,
] as const) satisfies readonly SUPPORTED_EXCHANGES[];

export type AirdropFarmingPerpsExchangeId = (typeof SUPPORTED_AIRDROP_FARMING_PERPS_EXCHANGES)[number];

/**
 * Spot exchanges that support the Airdrop Farming feature.
 */
export const SUPPORTED_AIRDROP_FARMING_SPOT_EXCHANGES = Object.freeze([
  SUPPORTED_EXCHANGES.MEXC,
  SUPPORTED_EXCHANGES.KUCOIN,
  SUPPORTED_EXCHANGES.CRYPTOCOM,
  SUPPORTED_EXCHANGES.BYBIT,
] as const) satisfies readonly SUPPORTED_EXCHANGES[];

export type AirdropFarmingSpotExchangeId = (typeof SUPPORTED_AIRDROP_FARMING_SPOT_EXCHANGES)[number];

/** All airdrop farming exchange IDs — perps + spot. Creatable venues only. */
export const SUPPORTED_AIRDROP_FARMING_EXCHANGES = Object.freeze([
  ...SUPPORTED_AIRDROP_FARMING_PERPS_EXCHANGES,
  ...SUPPORTED_AIRDROP_FARMING_SPOT_EXCHANGES,
] as const);

export type AirdropFarmingExchangeId = (typeof SUPPORTED_AIRDROP_FARMING_EXCHANGES)[number];

/**
 * Venues that were delisted (dead HIP-3 builders, shut-down exchanges). New bots are rejected,
 * but bots created before delisting still exist and must load.
 */
export const DELISTED_AIRDROP_FARMING_EXCHANGES = Object.freeze([
  SUPPORTED_EXCHANGES.FLX,
  SUPPORTED_EXCHANGES.VNTL,
  SUPPORTED_EXCHANGES.KM,
  SUPPORTED_EXCHANGES.CASH,
  SUPPORTED_EXCHANGES.DANGO,
  SUPPORTED_EXCHANGES.HYNA,
] as const) satisfies readonly SUPPORTED_EXCHANGES[];

export type DelistedAirdropFarmingExchangeId = (typeof DELISTED_AIRDROP_FARMING_EXCHANGES)[number];

/** Every exchangeId a persisted bot row may hold: creatable + delisted. Read paths only. */
export const PERSISTED_AIRDROP_FARMING_EXCHANGES = Object.freeze([
  ...SUPPORTED_AIRDROP_FARMING_EXCHANGES,
  ...DELISTED_AIRDROP_FARMING_EXCHANGES,
] as const);

export type PersistedAirdropFarmingExchangeId = (typeof PERSISTED_AIRDROP_FARMING_EXCHANGES)[number];

export const DELISTED_AIRDROP_FARMING_EXCHANGE_SET: ReadonlySet<string> = new Set(DELISTED_AIRDROP_FARMING_EXCHANGES);

export function isDelistedAirdropFarmingExchange(exchangeId: string): exchangeId is DelistedAirdropFarmingExchangeId {
  return DELISTED_AIRDROP_FARMING_EXCHANGE_SET.has(exchangeId);
}

/**
 * Exchanges that support spot airdrop farming (Set for runtime checks).
 */
export const AIRDROP_FARMING_SPOT_SUPPORTED_EXCHANGES: ReadonlySet<string> = new Set(
  SUPPORTED_AIRDROP_FARMING_SPOT_EXCHANGES,
);
